import React from 'react';
import './LowboyDispatchBoard.scss';
import moment from 'moment';
import { withRouter } from 'react-router-dom';
import { LinearProgress } from '@mui/material';
import { util } from '../Util';
import { ApiRoutes } from '../ApiRoutes';
import { MomentDateFormat } from '../MomentDateFormat';
import LowboyResourcePane from './LowboyResourcePane';
import { DragDropContext } from '@hello-pangea/dnd';
import LowboyEquipmentPane from './LowboyEquipmentPane';
import LowboyAssignmentPane from './LowboyAssignmentPane';
import { getNumbersFromEndOfString } from '../utilities/StringHelpers';
import { toasty } from '../common/forms/FormElements';

class LowboyDispatchBoard extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            selectedEquipmentTypeId: null,
            equipmentTypes: [],
            equipments: [],
            lowboyResources: [],
            lowboyAssignments: [],
            lowboyEmployees: [],
            activeDate: moment(new Date()).add(1, 'day'),
            loading: true,
            dragging: false,
            draggableId: null,
            nameFilter: null,
            equipmentsLoading: true,
            resourcesLoading: true,
            assignmentsLoading: true,
            resourcingCounts: [],
            equipmentMoves: [],
            equipmentLocations: [],
            knownLocations: [],
        };

        this.fetchResourcesAndAssignments =
            this.fetchResourcesAndAssignments.bind(this);
    }

    componentDidMount() {
        this.populateState();
    }

    getApiDate() {
        const { activeDate } = this.state;
        return moment(activeDate).format(MomentDateFormat.ApiUrlRequest);
    }

    async fetchResourcesAndAssignments() {
        this.setState({
            equipmentsLoading: true,
            resourcesLoading: true,
            assignmentsLoading: true,
        });

        const apiDate = this.getApiDate();

        const [
            lowboyResources,
            lowboyAssignments,
            lowboyEmployees,
            resourcingCounts,
            equipmentMoves,
            equipmentLocations,
        ] = await Promise.all([
            util.fetch.js(ApiRoutes.lowboyResource.for(apiDate)),
            util.fetch.js(ApiRoutes.lowboyAssignment.for(apiDate)),
            util.fetch.js(ApiRoutes.lowboyEmployee.for(apiDate)),
            util.fetch.js(ApiRoutes.lowboyResource.counts(apiDate)),
            util.fetch.js(ApiRoutes.lowboyEquipment.moves(apiDate)),
            util.fetch.js(ApiRoutes.equipmentLocation.for(apiDate)),
        ]);

        this.setState({
            lowboyResources,
            lowboyAssignments,
            lowboyEmployees,
            loading: false,
            equipmentsLoading: false,
            resourcesLoading: false,
            assignmentsLoading: false,
            resourcingCounts,
            equipmentMoves,
            equipmentLocations,
        });
    }

    onEquipmentLocationUpdated = async () => {
        this.setState({
            equipmentsLoading: true,
        });

        const apiDate = this.getApiDate();

        const [equipmentLocations] = await Promise.all([
            util.fetch.js(ApiRoutes.equipmentLocation.for(apiDate)),
        ]);

        this.setState({
            equipmentsLoading: false,
            equipmentLocations,
        });
    };

    async populateState() {
        const [
            equipments,
            allEquipmentTypes,
            equipmentTypeIds,
            knownLocations,
        ] = await Promise.all([
            util.fetch.js(ApiRoutes.lowboyEquipment.all()),
            util.fetch.js(ApiRoutes.equipmentType.all()),
            util.fetch.js(ApiRoutes.lowboyEquipmentType.getIdsRequiringMoves()),
            util.fetch.js(ApiRoutes.knownLocation.all()),
        ]);

        const equipmentTypes = allEquipmentTypes.filter((et) =>
            equipmentTypeIds.includes(et.id)
        );

        this.setState({ equipments, equipmentTypes, knownLocations }, () =>
            this.fetchResourcesAndAssignments()
        );
    }

    onDateChange = (e) => {
        if (!e || !e.target || !e.target.value) {
            return;
        }

        const activeDate = moment(e.target.value).toDate();

        this.setState({ activeDate }, () => {
            this.fetchResourcesAndAssignments();
        });
    };

    onNameFilterChange = (e) => {
        this.setState({ nameFilter: e.target.value });
    };

    onEquipmentTypeChange = (e) => {
        const selectedEquipmentTypeId = e && e.id ? e.id : null;

        this.setState({ selectedEquipmentTypeId });
    };

    onBeforeDragCapture = (draggable) => {
        this.setState({
            draggableId: draggable.draggableId,
        });
    };

    potentiallyAddLowboyCargo = async (
        equipmentHandle,
        lowboyResourceHandle
    ) => {
        if (
            !equipmentHandle ||
            !lowboyResourceHandle ||
            !lowboyResourceHandle.droppableId
        ) {
            return;
        }

        const { equipments, lowboyResources } = this.state;

        const lowboyResourceId = getNumbersFromEndOfString(
            lowboyResourceHandle.droppableId
        );

        const equipment = equipments[equipmentHandle.index];

        const lowboyResource = lowboyResources.find(
            (lr) => lr.id == lowboyResourceId
        );

        if (
            !equipment ||
            !lowboyResource ||
            equipment.equipmentTypeId != lowboyResource.equipmentTypeId
        ) {
            return;
        }

        this.setState({ equipmentsLoading: true, resourcesLoading: true });

        const data = {
            equipmentId: equipment.id,
            lowboyResourceId: lowboyResource.id,
        };

        try {
            await util.fetch.andGetResponse(
                util.fetch.types.post,
                ApiRoutes.lowboyCargo.add(),
                data
            );

            await this.fetchResourcesAndAssignments();
        } catch {
            await this.setState({
                equipmentsLoading: false,
                resourcesLoading: false,
            });
        }
    };

    onRemoveCargo = async (dataContainer) => {
        if (!dataContainer) {
            return;
        }

        const { lowboyAssignmentId } = dataContainer;

        if (!lowboyAssignmentId) {
            return;
        }

        this.setState({
            equipmentsLoading: true,
            resourcesLoading: true,
        });

        try {
            await util.fetch.andGetResponse(
                util.fetch.types.delete,
                ApiRoutes.lowboyCargo.remove(lowboyAssignmentId)
            );

            await this.fetchResourcesAndAssignments();
        } catch (e) {
            console.error({ e });

            this.setState({
                equipmentsLoading: false,
                resourcesLoading: false,
            });
        }
    };

    onRemoveAssignment = async (dataContainer) => {
        if (!dataContainer) {
            return;
        }

        const { lowboyAssignmentId } = dataContainer;

        if (!lowboyAssignmentId) {
            return;
        }

        this.setState({ resourcesLoading: true, assignmentsLoading: true });

        try {
            await util.fetch.andGetResponse(
                util.fetch.types.post,
                ApiRoutes.lowboyAssignment.remove(lowboyAssignmentId),
                { lowboyAssignmentId }
            );

            await this.fetchResourcesAndAssignments();
        } catch (e) {
            console.error({ e });

            this.setState({
                resourcesLoading: false,
                assignmentsLoading: false,
            });
        }
    };

    async potentiallyAssignToEmployee(draggableId, employeeHandle) {
        const lowboyAssignmentId = getNumbersFromEndOfString(draggableId);

        const employeeId = getNumbersFromEndOfString(
            employeeHandle.droppableId
        );

        if (!lowboyAssignmentId || !employeeId) {
            return;
        }

        this.setState({
            resourcesLoading: true,
            assignmentsLoading: true,
        });

        const data = { lowboyAssignmentId, employeeId };

        try {
            await util.fetch.andGetResponse(
                util.fetch.types.post,
                ApiRoutes.lowboyAssignment.add(),
                data
            );

            await this.fetchResourcesAndAssignments();
        } catch (e) {
            console.error({ e });

            this.setState({
                resourcesLoading: false,
                assignmentsLoading: false,
            });
        }
    }

    // https://codesandbox.io/p/sandbox/k260nyxq9v?file=%2Findex.js%3A18%2C17
    reorder(list, startIndex, endIndex) {
        const result = Array.from(list);
        const [removed] = result.splice(startIndex, 1);
        result.splice(endIndex, 0, removed);

        return result;
    }

    async reorderAssignments(
        sourceHandle,
        destinationHandle,
        assignmentDraggableId
    ) {
        const employeeId = getNumbersFromEndOfString(sourceHandle.droppableId);

        const destinationEmployeeId = getNumbersFromEndOfString(
            destinationHandle.droppableId
        );

        // no dragging between drivers
        if (employeeId != destinationEmployeeId) {
            return;
        }

        // unmoved
        if (sourceHandle.index == destinationHandle.index) {
            return;
        }

        const { lowboyEmployees, activeDate } = this.state;

        const lowboyEmployee = lowboyEmployees.find((e) => e.id == employeeId);

        const assignmentId = getNumbersFromEndOfString(assignmentDraggableId);

        const { lowboyAssignmentIds } = lowboyEmployee;

        if (!lowboyAssignmentIds.includes(assignmentId)) {
            return;
        }

        const updatedLowboyAssignmentIds = this.reorder(
            lowboyAssignmentIds,
            sourceHandle.index,
            destinationHandle.index
        );

        this.setState({
            equipmentsLoading: true,
            resourcesLoading: true,
            assignmentsLoading: true,
        });

        const data = {
            employeeId,
            eventDate: activeDate,
            updatedLowboyAssignmentIds,
        };

        try {
            await util.fetch.andGetResponse(
                util.fetch.types.post,
                ApiRoutes.lowboyAssignment.reorder(),
                data
            );

            await this.fetchResourcesAndAssignments();
        } catch (e) {
            console.error({ e });

            this.setState({
                equipmentsLoading: false,
                resourcesLoading: false,
                assignmentsLoading: false,
            });
        }
    }

    onDragEnd = async (result) => {
        const { source, destination, draggableId } = result;

        if (source && destination) {
            if (
                source.droppableId === 'equipment-droppable' &&
                destination.droppableId.startsWith('resource-droppable')
            ) {
                await this.potentiallyAddLowboyCargo(source, destination);
            } else if (
                draggableId.startsWith('resource-draggable') &&
                destination.droppableId.startsWith('employee-droppable')
            ) {
                await this.potentiallyAssignToEmployee(
                    draggableId,
                    destination
                );
            } else if (
                source.droppableId.startsWith('employee-droppable') &&
                destination.droppableId.startsWith('employee-droppable')
            ) {
                await this.reorderAssignments(source, destination, draggableId);
            }
        }

        this.setState({ dragging: false, draggableId: null });
    };

    onDragStart = () => {
        this.setState({ dragging: true });
    };

    onDateSelect = (date) => {
        const activeDate = moment(date).toDate();
        this.setState({ activeDate }, () => {
            this.fetchResourcesAndAssignments();
        });
    };

    onResourceCardClick = (equipmentTypeId) => {
        this.setState({ selectedEquipmentTypeId: equipmentTypeId });
    };

    onSendNotificationClick = async () => {
        const { resourcesLoading, assignmentsLoading } = this.state;

        if (resourcesLoading || assignmentsLoading) {
            return;
        }

        this.setState({
            resourcesLoading: true,
            assignmentsLoading: true,
        });

        try {
            const apiDate = this.getApiDate();
            await util.fetch.andGetResponse(
                util.fetch.types.post,
                ApiRoutes.lowboyAssignment.notify(apiDate),
                { apiDate }
            );

            await this.fetchResourcesAndAssignments();

            const { eventDate } = this.state;

            toasty.success(
                'Lowboy notifications sent',
                `Lowboy assignment notifications for ${moment(eventDate).format(MomentDateFormat.WeekDayNameAndDate)} have been sent.`
            );
        } catch (e) {
            console.error({ e });

            this.setState({
                resourcesLoading: false,
                assignmentsLoading: false,
            });
        }
    };

    render() {
        const { loading } = this.state;

        if (loading) {
            return <LinearProgress variant="indeterminate" color="secondary" />;
        }

        return (
            <div className="dispatch-board p-2">
                <DragDropContext
                    onDragStart={this.onDragStart}
                    onDragEnd={this.onDragEnd}
                    onBeforeCapture={this.onBeforeDragCapture}
                >
                    <div className="dispatch-container">
                        <LowboyEquipmentPane
                            onDateChange={this.onDateChange}
                            onEquipmentTypeChange={this.onEquipmentTypeChange}
                            onNameFilterChange={this.onNameFilterChange}
                            onRefreshClicked={this.fetchResourcesAndAssignments}
                            onEquipmentLocationUpdated={
                                this.onEquipmentLocationUpdated
                            }
                            {...this.state}
                        />

                        <LowboyResourcePane
                            {...this.state}
                            onRemoveCargo={this.onRemoveCargo}
                            onResourceCardClick={this.onResourceCardClick}
                        />

                        <LowboyAssignmentPane
                            {...this.state}
                            onRemoveAssignment={this.onRemoveAssignment}
                            onSendNotificationClick={
                                this.onSendNotificationClick
                            }
                            onDateSelect={this.onDateSelect}
                        />
                    </div>
                </DragDropContext>
            </div>
        );
    }
}

export default withRouter(LowboyDispatchBoard);
